import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';
import { formatLocalDateTime, parseServerDateTime } from './timezone';

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  padding: '20px',
  boxSizing: 'border-box'
};

const contentStyle = {
  maxWidth: '900px',
  margin: '0 auto',
  width: '100%',
  background: 'var(--bg-tertiary)',
  padding: '30px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px var(--shadow-color)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '20px'
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '13px',
  verticalAlign: 'middle'
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  marginBottom: '12px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '4px',
  fontWeight: 'bold',
  fontSize: '12px',
  color: 'var(--text-secondary)'
};

const buttonStyle = {
  padding: '8px 16px',
  backgroundColor: 'var(--accent-color)',
  color: 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginRight: '8px',
  fontSize: '13px'
};

const dangerButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--error-color)',
  marginRight: 0
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--button-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: 'var(--bg-tertiary)',
  padding: '28px',
  borderRadius: '8px',
  width: '520px',
  maxWidth: '90%',
  boxShadow: '0 4px 20px var(--shadow-color)'
};

export default function DataStore({ onBack }) {
  const [entries, setEntries] = useState([]);
  const [namespaceFilter, setNamespaceFilter] = useState('');
  const [keyFilter, setKeyFilter] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null); // null = new entry
  const [formData, setFormData] = useState({ namespace: 'default', key: '', value: '', ttl_seconds: '' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { namespace, key }
  const [confirmClear, setConfirmClear] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (namespaceFilter) params.set('namespace', namespaceFilter);
      if (keyFilter) params.set('key_prefix', keyFilter);
      if (showExpired) params.set('include_expired', 'true');
      const res = await fetch(`${API_BASE_URL}/datastore?${params}`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to fetch entries');
      setEntries(await res.json());
    } catch (e) {
      setError(e.message);
    }
  }, [namespaceFilter, keyFilter, showExpired, token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openNew = () => {
    setEditEntry(null);
    setFormData({ namespace: 'default', key: '', value: '', ttl_seconds: '' });
    setError(null);
    setIsModalOpen(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setFormData({
      namespace: entry.namespace,
      key: entry.key,
      value: entry.value,
      ttl_seconds: ''
    });
    setError(null);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.key.trim()) { setError('Key is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        namespace: formData.namespace || 'default',
        key: formData.key.trim(),
        value: formData.value,
        ttl_seconds: formData.ttl_seconds ? parseInt(formData.ttl_seconds) : null
      };
      const res = await fetch(`${API_BASE_URL}/datastore`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setIsModalOpen(false);
      fetchEntries();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (namespace, key) => {
    try {
      const res = await fetch(`${API_BASE_URL}/datastore/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Delete failed');
      setConfirmDelete(null);
      fetchEntries();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleClear = async () => {
    try {
      const params = namespaceFilter ? `?namespace=${encodeURIComponent(namespaceFilter)}` : '';
      const res = await fetch(`${API_BASE_URL}/datastore${params}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Clear failed');
      setConfirmClear(false);
      fetchEntries();
    } catch (e) {
      setError(e.message);
    }
  };

  const namespaces = [...new Set(entries.map(e => e.namespace))].sort();

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    const d = parseServerDateTime(expiresAt);
    if (!d) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    const now = new Date();
    const expired = d < now;
    return (
      <span style={{ color: expired ? 'var(--error-color)' : 'var(--text-secondary)', fontSize: '11px' }}>
        {expired ? '⚠ ' : ''}{formatLocalDateTime(d)}
      </span>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', maxWidth: '900px', margin: '0 auto', width: '100%', paddingBottom: '12px' }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '22px', cursor: 'pointer', marginRight: '12px' }}>←</button>
        )}
        <h2 style={{ margin: 0, fontSize: '20px' }}>🗄️ Data Store</h2>
      </div>

      <div style={contentStyle}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: 0 }}>
          Persistent key-value storage for your workflows. Use the <strong>Data Store</strong> node to read and write entries at runtime. Data is scoped to your account and persists across workflow runs.
        </p>

        {error && (
          <div style={{ background: 'var(--error-bg, #3a1a1a)', color: 'var(--error-color)', padding: '10px 14px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {/* Filters + actions row */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <select
            value={namespaceFilter}
            onChange={e => setNamespaceFilter(e.target.value)}
            style={{ ...inputStyle, width: '180px', marginBottom: 0 }}
          >
            <option value="">All namespaces</option>
            {namespaces.map(ns => <option key={ns} value={ns}>{ns}</option>)}
          </select>
          <input
            style={{ ...inputStyle, width: '200px', marginBottom: 0 }}
            placeholder="Filter by key prefix..."
            value={keyFilter}
            onChange={e => setKeyFilter(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={showExpired} onChange={e => setShowExpired(e.target.checked)} />
            Show expired
          </label>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button style={buttonStyle} onClick={openNew}>+ New Entry</button>
            <button
              style={{ ...secondaryButtonStyle, color: 'var(--error-color)', borderColor: 'var(--error-color)' }}
              onClick={() => setConfirmClear(true)}
            >
              🗑 Clear{namespaceFilter ? ` "${namespaceFilter}"` : ' All'}
            </button>
          </div>
        </div>

        {/* Table */}
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px 0', fontSize: '14px' }}>
            No entries found. Use the <strong>Data Store</strong> node in a workflow or click <strong>+ New Entry</strong> to add one manually.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Namespace</th>
                <th style={thStyle}>Key</th>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Expires At</th>
                <th style={thStyle}>Updated</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={`${entry.namespace}::${entry.key}`} style={{ opacity: (() => {
                  const expiresAt = parseServerDateTime(entry.expires_at);
                  return expiresAt && expiresAt < new Date() ? 0.5 : 1;
                })() }}>
                  <td style={tdStyle}>
                    <code style={{ fontSize: '12px', background: 'var(--bg-input)', padding: '2px 6px', borderRadius: '3px' }}>{entry.namespace}</code>
                  </td>
                  <td style={tdStyle}>
                    <code style={{ fontSize: '12px' }}>{entry.key}</code>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: '260px' }}>
                    <code style={{ fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all', display: 'block', maxHeight: '48px', overflow: 'hidden' }}>
                      {entry.value.length > 120 ? entry.value.slice(0, 120) + '…' : entry.value}
                    </code>
                  </td>
                  <td style={tdStyle}>{formatExpiry(entry.expires_at)}</td>
                  <td style={{ ...tdStyle, fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                    {formatLocalDateTime(entry.updated_at)}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => openEdit(entry)}
                      style={{ background: 'none', border: '1px solid var(--border-color)', color: 'var(--accent-color)', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' }}
                    >Edit</button>
                    <button
                      onClick={() => setConfirmDelete({ namespace: entry.namespace, key: entry.key })}
                      style={{ background: 'none', border: '1px solid var(--error-color)', color: 'var(--error-color)', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
          {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
        </div>
      </div>

      {/* New / Edit modal */}
      {isModalOpen && (
        <div style={modalOverlayStyle} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px' }}>{editEntry ? 'Edit Entry' : 'New Entry'}</h3>

            {error && (
              <div style={{ color: 'var(--error-color)', marginBottom: '12px', fontSize: '13px' }}>{error}</div>
            )}

            <label style={labelStyle}>Namespace</label>
            <input
              style={inputStyle}
              value={formData.namespace}
              onChange={e => setFormData(f => ({ ...f, namespace: e.target.value }))}
              placeholder="default"
            />

            <label style={labelStyle}>Key</label>
            <input
              style={inputStyle}
              value={formData.key}
              onChange={e => setFormData(f => ({ ...f, key: e.target.value }))}
              placeholder="my-counter"
              disabled={!!editEntry}
            />

            <label style={labelStyle}>Value (JSON)</label>
            <textarea
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
              value={formData.value}
              onChange={e => setFormData(f => ({ ...f, value: e.target.value }))}
              placeholder={'"hello" or 42 or {"status": "active"}'}
            />

            <label style={labelStyle}>TTL (seconds, leave empty for no expiry)</label>
            <input
              style={inputStyle}
              type="number"
              value={formData.ttl_seconds}
              onChange={e => setFormData(f => ({ ...f, ttl_seconds: e.target.value }))}
              placeholder="e.g. 3600 for 1 hour"
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
              <button style={secondaryButtonStyle} onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button style={buttonStyle} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete entry */}
      {confirmDelete && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: '380px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Delete Entry</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Delete <code>{confirmDelete.namespace}::{confirmDelete.key}</code>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={secondaryButtonStyle} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={dangerButtonStyle} onClick={() => handleDelete(confirmDelete.namespace, confirmDelete.key)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm clear */}
      {confirmClear && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: '400px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Clear Data Store</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {namespaceFilter
                ? `Delete all entries in namespace "${namespaceFilter}"?`
                : 'Delete ALL data store entries? This cannot be undone.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={secondaryButtonStyle} onClick={() => setConfirmClear(false)}>Cancel</button>
              <button style={dangerButtonStyle} onClick={handleClear}>Clear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
